import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import './DocumentsPage.css';

const DocumentsPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeDocument, setActiveDocument] = useState(null);

  // Mock document data
  const allDocuments = [
    {
      id: 1,
      title: 'Purchase Agreement',
      address: '2450 Ashley Ave',
      type: 'Contract',
      category: 'Contracts',
      status: 'Signed',
      fileSize: '2.4 MB',
      date: 'Dec 15, 2025'
    },
    {
      id: 2,
      title: 'Home Inspection Report',
      address: '2450 Ashley Ave',
      type: 'Report',
      category: 'Reports',
      status: 'Review',
      fileSize: '5.1 MB',
      date: 'Dec 18, 2025'
    },
    {
      id: 3,
      title: 'Seller Disclosure',
      address: '892 Pine Road',
      type: 'Disclosure',
      category: 'Disclosures',
      status: 'Pending',
      fileSize: '1.2 MB',
      date: 'Dec 10, 2025'
    },
    {
      id: 4,
      title: 'Sales Contract',
      address: '1567 Oak Street',
      type: 'Contract',
      category: 'Contracts',
      status: 'Signed',
      fileSize: '3.8 MB',
      date: 'Dec 12, 2025'
    },
    {
      id: 5,
      title: 'Appraisal Report',
      address: '3421 Sunset Blvd',
      type: 'Report',
      category: 'Reports',
      status: 'Complete',
      fileSize: '4.5 MB',
      date: 'Dec 21, 2025'
    },
    {
      id: 6,
      title: 'Property Disclosure',
      address: '1567 Oak Street',
      type: 'Disclosure',
      category: 'Disclosures',
      status: 'Signed',
      fileSize: '900 KB',
      date: 'Dec 8, 2025'
    },
    {
      id: 7,
      title: 'Closing Documents',
      address: '567 Birch Drive',
      type: 'Contract',
      category: 'Contracts',
      status: 'Complete',
      fileSize: '6.2 MB',
      date: 'Nov 15, 2025'
    },
    {
      id: 8,
      title: 'Title Report',
      address: '890 Cedar Ridge',
      type: 'Report',
      category: 'Reports',
      status: 'Complete',
      fileSize: '2.8 MB',
      date: 'Sep 15, 2025'
    }
  ];

  // Filter documents based on active filter
  const filteredDocuments = activeFilter === 'All'
    ? allDocuments
    : allDocuments.filter(d => d.category === activeFilter);

  // Update active document when filter changes
  useEffect(() => {
    const filtered = activeFilter === 'All'
      ? allDocuments
      : allDocuments.filter(d => d.category === activeFilter);

    if (filtered.length > 0) {
      setActiveDocument(filtered[0]);
      setSelectedDocument(filtered[0].id);
    } else {
      setActiveDocument(null);
      setSelectedDocument(null);
    }
  }, [activeFilter]);

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document.id);
    setActiveDocument(document);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Signed': '#10b981',
      'Review': '#f59e0b',
      'Pending': '#f97316',
      'Complete': '#6b7280'
    };
    return statusColors[status] || '#6b7280';
  };

  return (
    <div className="documents-page">

      <div className="documents-container">
        {/* Left Sidebar - Documents List */}
        <div className="documents-sidebar">
          <div className="documents-sidebar-header">
            <h1 className="documents-sidebar-title">Documents</h1>
          </div>

          {/* Filter Tabs */}
          <div className="documents-filter-tabs">
            <button
              className={`documents-filter-tab ${activeFilter === 'All' ? 'active' : ''}`}
              onClick={() => setActiveFilter('All')}
            >
              All
            </button>
            <button
              className={`documents-filter-tab ${activeFilter === 'Contracts' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Contracts')}
            >
              Contracts
            </button>
            <button
              className={`documents-filter-tab ${activeFilter === 'Reports' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Reports')}
            >
              Reports
            </button>
            <button
              className={`documents-filter-tab ${activeFilter === 'Disclosures' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Disclosures')}
            >
              Disclosures
            </button>
          </div>

          <div className="documents-search-wrapper">
            <input
              type="text"
              className="documents-search-input"
              placeholder="Search documents..."
            />
          </div>

          <div className="documents-list">
            {filteredDocuments.map((document) => (
              <div
                key={document.id}
                className={`documents-list-item ${selectedDocument === document.id ? 'active' : ''}`}
                onClick={() => handleDocumentSelect(document)}
              >
                <div className="documents-list-item-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <div className="documents-list-item-content">
                  <h3 className="documents-list-item-title">{document.title}</h3>
                  <div className="documents-list-item-address">
                    <span>🏠</span>
                    <span>{document.address}</span>
                  </div>
                  <div className="documents-list-item-meta">
                    <span
                      className="documents-list-item-status"
                      style={{ backgroundColor: `${getStatusColor(document.status)}20`, color: getStatusColor(document.status) }}
                    >
                      {document.status}
                    </span>
                    <span className="documents-list-item-size">{document.fileSize}</span>
                    <span className="documents-list-item-date">• {document.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Document Details */}
        <div className="documents-main">
          {activeDocument ? (
            <div className="documents-content-wrapper">
              {/* Document Header Card */}
              <div className="documents-detail-card">
                <div className="documents-detail-header">
                  <div className="documents-detail-header-content">
                    <div className="documents-detail-type">{activeDocument.type}</div>
                    <h2 className="documents-detail-title">{activeDocument.title}</h2>
                    <div className="documents-detail-address">
                      <span>🏠</span>
                      <span>{activeDocument.address}</span>
                    </div>
                    <div className="documents-detail-info">
                      <span className="documents-detail-date">
                        <span>📅</span>
                        <span>{activeDocument.date}</span>
                      </span>
                      <span className="documents-detail-size">
                        <span>📦</span>
                        <span>{activeDocument.fileSize}</span>
                      </span>
                    </div>
                  </div>
                  <div className="documents-detail-status-wrapper">
                    <span
                      className="documents-detail-status"
                      style={{ backgroundColor: `${getStatusColor(activeDocument.status)}20`, color: getStatusColor(activeDocument.status) }}
                    >
                      {activeDocument.status}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="documents-actions">
                  <button className="documents-action-btn documents-action-btn-primary">
                    📥 Download
                  </button>
                  <button className="documents-action-btn documents-action-btn-secondary">
                    📤 Share
                  </button>
                  <button className="documents-action-btn documents-action-btn-secondary">
                    🖨️ Print
                  </button>
                </div>
              </div>

              {/* Document Preview Card */}
              <div className="documents-preview-card">
                <div className="documents-preview-container">
                  <div className="documents-preview-icon">📄</div>
                  <h3 className="documents-preview-title">Document Preview</h3>
                  <p className="documents-preview-subtitle">{activeDocument.title} • {activeDocument.fileSize}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="documents-no-selection">
              <p>Select a document to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsPage;
